/**
 * WidgetConfigPanel Component (Story 5.6, Task 5.4)
 *
 * Right sidebar for configuring the selected widget.
 * Functional controls: title editing, type label, column/row span selectors.
 * Shown only when a widget is selected.
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import {
  Hash,
  TrendingUp,
  BarChart2,
  PieChart as PieChartIcon,
  Table,
  Type,
  Newspaper,
  BookOpen,
  Info,
  Plus,
  Pencil,
  Trash2,
  CalendarDays,
  Rocket,
  DollarSign,
  AlertTriangle,
  Flag,
  Filter,
  Users,
  Heading2,
  AlignLeft,
  FileText,
  Minus,
  Calculator,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { Input, Switch } from "@provenleap/ui";
import type { WidgetConfig } from "../../lib/templateUtils";
import {
  BACKGROUND_STYLE_OPTIONS,
  ANNOTATION_COLORS,
  ANNOTATION_COLOR_OPTIONS,
  ANNOTATION_ICON_OPTIONS,
} from "@provenleap/types/report-rendering";
import type { AnnotationConfig, AnnotationColorKey, AnnotationIconKey } from "@provenleap/types/report-rendering";
import { WIDGET_FONT_OPTIONS, DEFAULT_WIDGET_FONT } from "@provenleap/types";
import { Popover, PopoverTrigger, PopoverContent } from "@provenleap/ui";
import { Calendar } from "@provenleap/ui";
import { METRIC_PLATFORM_MAP } from "../../lib/templateUtils";
import { Image as ImageIcon, Code, Crosshair } from "lucide-react";
import { ImageConfigSection, EmbedConfigSection } from "./widgetMediaForms";
import { ContentBlockConfigSection } from "./contentBlockForms";
import { useAvailableMetrics } from "../../hooks/useAvailableMetrics";
import { DataSourceSection, DATA_CONNECTED_WIDGET_TYPES } from "./DataSourceSection";
import { metricKeysFromConfig } from "../../lib/widgetCompatibility";
import { ColorPickerField } from "./ColorPickerField";
import { PxField } from "./PxField";
import { SelectField } from "./SelectField";
import { NAVY_SERIES } from "./widgets/chartWidgetShell";
// Story 37.5: the three heaviest config sub-forms (custom-metric / benchmark /
// goal) + their helpers MOVED to the shared `widgetDataForms` leaf so this panel
// and the configure-then-add category panels consume ONE definition (anti-drift).
// GROUPED_METRIC_OPTIONS + PLATFORM_LABELS + the GroupedMetricOption type are
// re-imported here because the dynamicGroupedOptions useMemo (the client-narrowing
// layer that STAYS in this panel) still references them.
import {
  CustomMetricConfigSection,
  BenchmarkConfigSection,
  GoalConfigSection,
  KpiConfigSection,
  GROUPED_METRIC_OPTIONS,
  PLATFORM_LABELS,
} from "./widgetDataForms";
import type { GroupedMetricOption } from "./widgetDataForms";

export const WIDGET_TYPE_INFO: Record<string, { label: string; icon: LucideIcon }> = {
  "cover-page": { label: "Cover Page", icon: BookOpen },
  kpi: { label: "KPI Card", icon: Hash },
  "line-chart": { label: "Line Chart", icon: TrendingUp },
  "bar-chart": { label: "Bar Chart", icon: BarChart2 },
  "pie-chart": { label: "Pie/Donut Chart", icon: PieChartIcon },
  "data-table": { label: "Data Table", icon: Table },
  "text-block": { label: "Text Block", icon: Type },
  "top-posts": { label: "Top Posts", icon: Newspaper },
  funnel: { label: "Funnel Chart", icon: Filter },
  demographic: { label: "Demographics", icon: Users },
  heading: { label: "Heading", icon: Heading2 },
  paragraph: { label: "Paragraph", icon: AlignLeft },
  "rich-text": { label: "Rich Text", icon: FileText },
  "spacer-divider": { label: "Spacer / Divider", icon: Minus },
  image: { label: "Image", icon: ImageIcon },
  embed: { label: "Embed", icon: Code },
  "custom-metric": { label: "Custom Metric", icon: Calculator },
  benchmark: { label: "Benchmark", icon: Crosshair },
  goal: { label: "Goal Tracker", icon: Flag },
  "rollup-table": { label: "Rollup Table", icon: Table },
};


const MAX_ANNOTATIONS = 8;

const ANNOTATION_ICON_MAP: Record<string, LucideIcon> = {
  rocket: Rocket,
  dollar: DollarSign,
  alert: AlertTriangle,
  flag: Flag,
};

const ANNOTATION_COLOR_LABELS: Record<AnnotationColorKey, string> = {
  blue: "Blue",
  green: "Green",
  orange: "Orange",
  red: "Red",
  gray: "Gray",
};

const ANNOTATION_ICON_LABELS: Record<AnnotationIconKey, string> = {
  rocket: "Launch",
  dollar: "Budget",
  alert: "Issue",
  flag: "Milestone",
};

function formatAnnotationDate(dateStr: string): string {
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return dateStr;
  }
}

/** Email platforms where open-rate metrics carry Apple MPP privacy caveats */
const EMAIL_SORT_PLATFORMS = new Set(["mailchimp", "klaviyo"]);

export interface WidgetConfigPanelProps {
  widget: WidgetConfig;
  onUpdate: (widgetId: string, changes: Partial<WidgetConfig>) => void;
  /** Date range start for cover page period display */
  reportDateRangeStart?: string;
  /** Date range end for cover page period display */
  reportDateRangeEnd?: string;
  /** Selected client name for cover page display */
  selectedClientName?: string;
  /** Client ID for fetching connected integrations (Story 5.34) */
  clientId?: string;
}

export function WidgetConfigPanel({ widget, onUpdate, reportDateRangeStart, reportDateRangeEnd, selectedClientName, clientId }: WidgetConfigPanelProps) {
  const [titleDraft, setTitleDraft] = useState(widget.title);
  // (Widget type + icon now shown in the panel header, not here.)

  // ── Settings search (Appearance) ────────────────────────────────
  // A quick filter over the Appearance settings so a long panel is navigable:
  // matches BOTH group headers (e.g. "Background & Border", "Title") AND the
  // individual field labels (e.g. "Border radius", "Title align"). A field shows
  // when the query hits its own label OR its group's name; a group shows when its
  // name matches OR any of its fields match.
  const [settingsSearch, setSettingsSearch] = useState("");
  const settingsQuery = settingsSearch.trim().toLowerCase();
  // Group → field labels, kept beside the JSX so group/field visibility stays in sync.
  // "Series" is line/bar-only; its fields are the bound metric keys + the words a
  // user would search for the line/bar colour pickers.
  const seriesSearchTokens = ["line color", "bar color", "line label", "bar label", "legend size", "legend marker", "marker size", "series", ...metricKeysFromConfig(widget.config as Record<string, unknown>)];
  const APPEARANCE_GROUPS: Record<string, string[]> = {
    "Background & Border": ["Fill color", "Border color", "Border radius"],
    Text: ["Text color", "Font", "Text size", "Trend line", "sparkline"],
    Series: seriesSearchTokens,
    Source: ["integration", "logo", "source", "data source badge", "icon size", "badge color", "badge font", "badge size"],
    Title: ["Title name", "Subtitle", "Title color", "Title size", "Title font", "Title align"],
  };
  const fieldVisible = (label: string, group: string) =>
    !settingsQuery || label.toLowerCase().includes(settingsQuery) || group.toLowerCase().includes(settingsQuery);
  const groupVisible = (group: string) =>
    !settingsQuery ||
    group.toLowerCase().includes(settingsQuery) ||
    (APPEARANCE_GROUPS[group] ?? []).some((l) => l.toLowerCase().includes(settingsQuery));
  // The Data source block (Integration / Metric / Group By) is a separate child;
  // filter it as one unit by the words a user would type for it. "account(s)" /
  // "aggregation" reveal the Story 32.6 aggregation indicator + override picker.
  const DATA_SOURCE_KEYWORDS = ["data source", "integration", "metric", "group by", "dimension", "account", "accounts", "aggregation"];
  const dataSourceVisible =
    !settingsQuery || DATA_SOURCE_KEYWORDS.some((k) => k.includes(settingsQuery) || settingsQuery.includes(k));

  // Story 5.35: Dynamic metric grouping from connected integrations
  const { allMetrics } = useAvailableMetrics(clientId);
  const dynamicGroupedOptions = useMemo<GroupedMetricOption[]>(() => {
    if (!clientId || allMetrics.length === 0) return GROUPED_METRIC_OPTIONS;
    const groups: Record<string, string[]> = {};
    for (const metric of allMetrics) {
      const platformKey = metric.platform;
      if (!groups[platformKey]) groups[platformKey] = [];
      groups[platformKey]!.push(metric.key);
    }
    return Object.entries(groups).map(([platform, metrics]) => ({
      platform,
      label: PLATFORM_LABELS[platform] ?? platform,
      metrics: metrics.sort(),
    }));
  }, [clientId, allMetrics]);

  // Dynamic metric→platform map for Goal widget (uses flat map instead of grouped)
  const dynamicMetricPlatformMap = useMemo<Record<string, string>>(() => {
    if (!clientId || allMetrics.length === 0) return METRIC_PLATFORM_MAP;
    const map: Record<string, string> = {};
    for (const metric of allMetrics) {
      if (!(metric.key in map)) {
        map[metric.key] = metric.platform;
      }
    }
    return map;
  }, [clientId, allMetrics]);

  // Sync draft with widget when selection changes (widget.id is the key)
  useEffect(() => {
    setTitleDraft(widget.title);
  }, [widget.id, widget.title]);

  const handleTitleCommit = useCallback(() => {
    // Allow an EMPTY title to commit — like the subtitle, clearing Title name
    // simply hides the title on the canvas + PDF (no separate "Show title" toggle).
    const trimmed = titleDraft.trim();
    if (trimmed !== widget.title) {
      onUpdate(widget.id, { title: trimmed });
    } else {
      setTitleDraft(widget.title);
    }
  }, [titleDraft, widget.title, widget.id, onUpdate]);

  const handleTitleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleTitleCommit();
      }
    },
    [handleTitleCommit]
  );

  const handleConfigChange = useCallback(
    (key: string, value: string | number) => {
      onUpdate(widget.id, {
        config: { ...widget.config, [key]: value },
      });
    },
    [widget.id, widget.config, onUpdate]
  );

  const handleTopPostsConfigChange = handleConfigChange;

  // Per-widget appearance — merges into `config.style` ({ fillColor, borderColor,
  // borderRadius }); applied on the canvas card AND mirrored in the PDF renderer.
  const widgetStyle = (widget.config?.style ?? {}) as {
    fillColor?: string;
    borderColor?: string;
    borderRadius?: number;
    textColor?: string;
    fontFamily?: string;
    fontSize?: number;
  };
  const handleStyleChange = useCallback(
    (key: "fillColor" | "borderColor" | "borderRadius" | "textColor" | "fontFamily" | "fontSize" | "legendFontSize" | "legendMarker" | "legendMarkerSize" | "integrationBadge" | "hideSparkline", value: string | number | boolean | undefined) => {
      const prevStyle = (widget.config?.style ?? {}) as Record<string, unknown>;
      const nextStyle = { ...prevStyle };
      if (value === undefined || value === "" || value === false) delete nextStyle[key];
      else nextStyle[key] = value;
      onUpdate(widget.id, { config: { ...widget.config, style: nextStyle } });
    },
    [widget.id, widget.config, onUpdate]
  );

  // Per-widget TITLE styling — nests under config.style.title
  // ({ color, fontSize, fontFamily, align }); applied by the shared shell
  // (canvas) and the PDF title renderer.
  const titleStyle = ((widget.config?.style as { title?: Record<string, unknown> } | undefined)?.title ?? {}) as {
    color?: string;
    fontSize?: number;
    fontFamily?: string;
    align?: "left" | "center" | "right";
  };
  const handleTitleStyleChange = useCallback(
    (key: "color" | "fontSize" | "fontFamily" | "align", value: string | number | boolean | undefined) => {
      const prevStyle = (widget.config?.style ?? {}) as Record<string, unknown>;
      const prevTitle = (prevStyle.title ?? {}) as Record<string, unknown>;
      const nextTitle = { ...prevTitle };
      if (value === undefined || value === "" || value === false) delete nextTitle[key];
      else nextTitle[key] = value;
      const nextStyle = { ...prevStyle };
      if (Object.keys(nextTitle).length === 0) delete nextStyle.title;
      else nextStyle.title = nextTitle;
      onUpdate(widget.id, { config: { ...widget.config, style: nextStyle } });
    },
    [widget.id, widget.config, onUpdate]
  );

  // Integration badge styling — nests under config.style.badge
  // ({ color, fontSize, fontFamily, iconSize }); applied by the shared shell
  // (canvas) and the PDF badge renderer.
  const badgeStyle = ((widget.config?.style as { badge?: Record<string, unknown> } | undefined)?.badge ?? {}) as {
    color?: string;
    fontSize?: number;
    fontFamily?: string;
    iconSize?: number;
  };
  const handleBadgeStyleChange = useCallback(
    (key: "color" | "fontSize" | "fontFamily" | "iconSize", value: string | number | undefined) => {
      const prevStyle = (widget.config?.style ?? {}) as Record<string, unknown>;
      const prevBadge = (prevStyle.badge ?? {}) as Record<string, unknown>;
      const nextBadge = { ...prevBadge };
      if (value === undefined || value === "") delete nextBadge[key];
      else nextBadge[key] = value;
      const nextStyle = { ...prevStyle };
      if (Object.keys(nextBadge).length === 0) delete nextStyle.badge;
      else nextStyle.badge = nextBadge;
      onUpdate(widget.id, { config: { ...widget.config, style: nextStyle } });
    },
    [widget.id, widget.config, onUpdate]
  );

  // Per-SERIES line/bar styling — one entry per bound metric, stored under
  // config.style.seriesColors = { [metricKey]: hex } and .seriesLabels =
  // { [metricKey]: friendlyName }. The renderer reads each per series and falls
  // back to the default palette / humanized key for any unset metric.
  const seriesColors = ((widget.config?.style as { seriesColors?: Record<string, string> } | undefined)?.seriesColors ?? {}) as Record<string, string>;
  const seriesLabels = ((widget.config?.style as { seriesLabels?: Record<string, string> } | undefined)?.seriesLabels ?? {}) as Record<string, string>;
  // Shared writer for a `style[mapKey]` keyed-by-metric map (colors or labels).
  const handleSeriesMapChange = useCallback(
    (mapKey: "seriesColors" | "seriesLabels", metricKey: string, value: string | undefined) => {
      const prevStyle = (widget.config?.style ?? {}) as Record<string, unknown>;
      const prevMap = (prevStyle[mapKey] ?? {}) as Record<string, string>;
      const nextMap = { ...prevMap };
      if (value === undefined || value === "") delete nextMap[metricKey];
      else nextMap[metricKey] = value;
      const nextStyle = { ...prevStyle };
      if (Object.keys(nextMap).length === 0) delete nextStyle[mapKey];
      else nextStyle[mapKey] = nextMap;
      onUpdate(widget.id, { config: { ...widget.config, style: nextStyle } });
    },
    [widget.id, widget.config, onUpdate],
  );
  const handleSeriesColorChange = useCallback(
    (metricKey: string, value: string | undefined) => handleSeriesMapChange("seriesColors", metricKey, value),
    [handleSeriesMapChange],
  );
  const handleSeriesLabelChange = useCallback(
    (metricKey: string, value: string | undefined) => handleSeriesMapChange("seriesLabels", metricKey, value),
    [handleSeriesMapChange],
  );

  // colSpan/rowSpan/auto-size handlers removed (Epic 37): the widget is freely
  // drag-resized on the canvas, so those manual span controls no longer exist.
  const isCoverPage = widget.type === "cover-page";
  const isKpi = widget.type === "kpi";
  const isLineChart = widget.type === "line-chart";
  // Charts with X/Y axes: the "Text" appearance group has no body text to style,
  // so it's relabeled "Axis labels" and drives the axis tick color/font/size.
  const isAxisChart = widget.type === "line-chart" || widget.type === "bar-chart";
  // Series widgets (line/bar) get per-line colour pickers, one per bound metric.
  const isSeriesChart = widget.type === "line-chart" || widget.type === "bar-chart";
  const seriesMetricKeys = isSeriesChart ? metricKeysFromConfig(widget.config as Record<string, unknown>) : [];
  const textGroupLabel = isAxisChart ? "Axis labels" : "Text";
  // Data-connected widgets can show an integration badge (logo / +name / hidden).
  const isDataConnected = DATA_CONNECTED_WIDGET_TYPES.includes(widget.type);
  const hasBoundIntegration = typeof (widget.config as { integrationId?: string } | undefined)?.integrationId === "string"
    && (widget.config as { integrationId?: string }).integrationId !== "";
  // Multi-integration chart: the badge shows ALL logos (no single name), so the
  // badge TEXT controls (color/font/size) don't apply — only icon size + mode.
  const badgeMultiSource =
    new Set(Object.values((widget.config as { metricIntegrations?: Record<string, string> } | undefined)?.metricIntegrations ?? {})).size > 1;

  // ── Annotations state (Story 5.17) ──────────────────────────────
  const [editingAnnotationId, setEditingAnnotationId] = useState<string | null>(null);
  const [showAnnotationForm, setShowAnnotationForm] = useState(false);

  // Form state
  const [formDate, setFormDate] = useState<Date | undefined>(undefined);
  const [formLabel, setFormLabel] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formColor, setFormColor] = useState<AnnotationColorKey>("blue");
  const [formIcon, setFormIcon] = useState<AnnotationIconKey | "">("");
  const [formError, setFormError] = useState("");

  const annotations = useMemo(() => {
    const config = widget.config as Record<string, unknown>;
    const arr = config.annotations as AnnotationConfig[] | undefined;
    if (!Array.isArray(arr)) return [];
    return [...arr].sort((a, b) => a.date.localeCompare(b.date));
  }, [widget.config]);

  // Reset form state when widget changes
  useEffect(() => {
    setShowAnnotationForm(false);
    setEditingAnnotationId(null);
    setFormError("");
  }, [widget.id]);

  const dateRangeStart = reportDateRangeStart ? new Date(reportDateRangeStart + "T00:00:00") : undefined;
  const dateRangeEnd = reportDateRangeEnd ? new Date(reportDateRangeEnd + "T00:00:00") : undefined;

  const resetForm = useCallback(() => {
    setFormDate(undefined);
    setFormLabel("");
    setFormDescription("");
    setFormColor("blue");
    setFormIcon("");
    setFormError("");
    setEditingAnnotationId(null);
    setShowAnnotationForm(false);
  }, []);

  const handleAnnotationAdd = useCallback(() => {
    setEditingAnnotationId(null);
    setFormDate(undefined);
    setFormLabel("");
    setFormDescription("");
    setFormColor("blue");
    setFormIcon("");
    setFormError("");
    setShowAnnotationForm(true);
  }, []);

  const handleAnnotationEdit = useCallback((annotation: AnnotationConfig) => {
    setEditingAnnotationId(annotation.id);
    setFormDate(new Date(annotation.date + "T00:00:00"));
    setFormLabel(annotation.label);
    setFormDescription(annotation.description ?? "");
    setFormColor(annotation.color);
    setFormIcon(annotation.icon ?? "");
    setFormError("");
    setShowAnnotationForm(true);
  }, []);

  const handleAnnotationDelete = useCallback((annotationId: string) => {
    const config = widget.config as Record<string, unknown>;
    const current = (config.annotations as AnnotationConfig[] | undefined) ?? [];
    const updated = current.filter(a => a.id !== annotationId);
    onUpdate(widget.id, {
      config: { ...widget.config, annotations: updated },
    });
  }, [widget.id, widget.config, onUpdate]);

  const handleAnnotationSave = useCallback(() => {
    // Validate
    if (!formDate) {
      setFormError("Date is required");
      return;
    }
    const trimmedLabel = formLabel.trim();
    if (!trimmedLabel) {
      setFormError("Label is required");
      return;
    }
    if (trimmedLabel.length > 50) {
      setFormError("Label must be 50 characters or fewer");
      return;
    }
    if (/[<>&]/.test(trimmedLabel)) {
      setFormError("Label cannot contain <, >, or &");
      return;
    }
    const trimmedDesc = formDescription.trim();
    if (trimmedDesc.length > 200) {
      setFormError("Description must be 200 characters or fewer");
      return;
    }
    if (trimmedDesc && /[<>&]/.test(trimmedDesc)) {
      setFormError("Description cannot contain <, >, or &");
      return;
    }

    // Date range validation
    if (dateRangeStart && formDate < dateRangeStart) {
      setFormError("Date must be within the report date range");
      return;
    }
    if (dateRangeEnd && formDate > dateRangeEnd) {
      setFormError("Date must be within the report date range");
      return;
    }

    const dateStr = formDate.toISOString().split("T")[0]!;
    const config = widget.config as Record<string, unknown>;
    const current = (config.annotations as AnnotationConfig[] | undefined) ?? [];

    const newAnnotation: AnnotationConfig = {
      id: editingAnnotationId ?? crypto.randomUUID(),
      date: dateStr,
      label: trimmedLabel,
      description: trimmedDesc || undefined,
      color: formColor,
      icon: formIcon || undefined,
    };

    let updated: AnnotationConfig[];
    if (editingAnnotationId) {
      updated = current.map(a => a.id === editingAnnotationId ? newAnnotation : a);
    } else {
      updated = [...current, newAnnotation];
    }

    onUpdate(widget.id, {
      config: { ...widget.config, annotations: updated },
    });
    resetForm();
  }, [formDate, formLabel, formDescription, formColor, formIcon, editingAnnotationId, widget.id, widget.config, onUpdate, resetForm, dateRangeStart, dateRangeEnd]);

  return (
    // Redesigned to UX-spec tokens (Epic 37): stacked sections, no own chrome
    // (the popover provides the card). Spacing = --space-4 (16px) between sections
    // via space-y-4; labels = --text-xs uppercase; values/inputs = --text-base.
    <div data-testid="widget-config-panel" className="w-full space-y-4">
      {/* Type section removed (Epic 37) — the widget type + icon are shown in the
          panel header instead. Field labels styled like the add-client "Industry"
          field: text-sm font-medium. */}

      {/* Settings search — sits at the very top of the panel (directly under the
          widget header). PIXEL-IDENTICAL to the WidgetPalette "Search
          integrations…" box: h-11 rounded-2xl, dark icon + dark placeholder,
          0.5px slate border, and an X clear button. Only for widgets that have an
          Appearance section (cover-page / spacer-divider don't). */}
      {!isCoverPage && widget.type !== "spacer-divider" && (
        <div className="relative mb-4">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground pointer-events-none"
          />
          <input
            type="text"
            aria-label="Search settings"
            data-testid="settings-search"
            value={settingsSearch}
            placeholder="Search settings..."
            onChange={(e) => setSettingsSearch(e.target.value)}
            className="flex h-11 w-full rounded-2xl bg-background px-3 py-2 pl-9 pr-9 text-[13.5px] text-foreground placeholder:text-foreground shadow-xs focus:outline-none focus-visible:outline-none"
            style={{ border: "0.5px solid var(--hairline)" }}
          />
          {settingsSearch && (
            <button
              type="button"
              onClick={() => setSettingsSearch("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-foreground/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded cursor-pointer"
            >
              <X size={16} />
            </button>
          )}
        </div>
      )}

      {/* Title — for spacer-divider only (it has no Appearance group, so its title
          input lives here). Cover-page has its own title field; every other widget
          edits its title inside the Appearance > Title group below. */}
      {widget.type === "spacer-divider" && (
        <div className="space-y-2">
          <label htmlFor="widget-title-input" className="text-[13.5px] font-medium">Title</label>
          <Input
            id="widget-title-input"
            data-testid="widget-title-input"
            type="text"
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={handleTitleCommit}
            onKeyDown={handleTitleKeyDown}
            className="rounded-lg text-[13.5px]"
          />
        </div>
      )}

      {/* Column Span / Row Span controls removed (Epic 37): the widget is freely
          drag-resized on the canvas, so manual span buttons are redundant. */}

      {/* Cover Page Config (Story 5.16) */}
      {isCoverPage && (
        <div className="border-t-[0.5px] border-input/70 pt-4 mt-2" data-testid="cover-page-config">
          <h4 className="text-[13.5px] font-medium text-foreground mb-3">
            Cover Page Settings
          </h4>

          {/* Cover title */}
          <div className="mb-3">
            <label htmlFor="cover-page-title" className="text-[13px] text-foreground">
              Report Title
            </label>
            <input
              id="cover-page-title"
              data-testid="cover-page-config-title"
              type="text"
              maxLength={100}
              value={(widget.config as Record<string, unknown>).title as string ?? ""}
              onChange={(e) => handleConfigChange("title", e.target.value)}
              className="mt-1 h-9 w-full rounded-lg border-[0.5px] border-input bg-background px-2.5 text-[13.5px] shadow-xs focus:outline-none focus-visible:outline-none"
            />
          </div>

          {/* Cover subtitle */}
          <div className="mb-3">
            <label htmlFor="cover-page-subtitle" className="text-[13px] text-foreground">
              Subtitle
            </label>
            <input
              id="cover-page-subtitle"
              data-testid="cover-page-config-subtitle"
              type="text"
              maxLength={150}
              value={(widget.config as Record<string, unknown>).subtitle as string ?? ""}
              onChange={(e) => handleConfigChange("subtitle", e.target.value)}
              className="mt-1 h-9 w-full rounded-lg border-[0.5px] border-input bg-background px-2.5 text-[13.5px] shadow-xs focus:outline-none focus-visible:outline-none"
            />
          </div>

          {/* Prepared by */}
          <div className="mb-3">
            <label htmlFor="cover-page-prepared-by" className="text-[13px] text-foreground">
              Prepared By
            </label>
            <input
              id="cover-page-prepared-by"
              data-testid="cover-page-config-prepared-by"
              type="text"
              maxLength={80}
              value={(widget.config as Record<string, unknown>).preparedBy as string ?? ""}
              onChange={(e) => handleConfigChange("preparedBy", e.target.value)}
              className="mt-1 h-9 w-full rounded-lg border-[0.5px] border-input bg-background px-2.5 text-[13.5px] shadow-xs focus:outline-none focus-visible:outline-none"
            />
          </div>

          {/* Background style */}
          <div className="mb-3">
            <label htmlFor="cover-page-bg-style" className="text-[13px] text-foreground">
              Background Style
            </label>
            <div className="mt-1">
              <SelectField
                ariaLabel="Background Style"
                testId="cover-page-config-bg-style"
                value={(widget.config as Record<string, unknown>).backgroundStyle as string ?? "gradient-blue"}
                onChange={(v) => handleConfigChange("backgroundStyle", v)}
                triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
                options={BACKGROUND_STYLE_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
              />
            </div>
          </div>

          {/* Client logo URL override */}
          <div className="mb-3">
            <label htmlFor="cover-page-client-logo" className="text-[13px] text-foreground">
              Client Logo URL
            </label>
            <input
              id="cover-page-client-logo"
              data-testid="cover-page-config-client-logo"
              type="url"
              placeholder="https://..."
              value={(widget.config as Record<string, unknown>).clientLogoUrl as string ?? ""}
              onChange={(e) => handleConfigChange("clientLogoUrl", e.target.value)}
              className="mt-1 h-9 w-full rounded-lg border-[0.5px] border-input bg-background px-2.5 text-[13.5px] shadow-xs focus:outline-none focus-visible:outline-none"
            />
          </div>

          {/* Agency logo URL override */}
          <div className="mb-3">
            <label htmlFor="cover-page-agency-logo" className="text-[13px] text-foreground">
              Agency Logo URL
            </label>
            <input
              id="cover-page-agency-logo"
              data-testid="cover-page-config-agency-logo"
              type="url"
              placeholder="https://..."
              value={(widget.config as Record<string, unknown>).agencyLogoUrl as string ?? ""}
              onChange={(e) => handleConfigChange("agencyLogoUrl", e.target.value)}
              className="mt-1 h-9 w-full rounded-lg border-[0.5px] border-input bg-background px-2.5 text-[13.5px] shadow-xs focus:outline-none focus-visible:outline-none"
            />
          </div>

          {/* Read-only derived fields */}
          <div className="border-t-[0.5px] border-input/70 pt-4 mt-3">
            <h4 className="text-[13.5px] font-medium text-foreground mb-2">
              Derived at render
            </h4>
            <div className="space-y-1.5 text-xs text-muted-foreground">
              <div data-testid="cover-page-derived-period">
                <span className="font-medium">Period: </span>
                {reportDateRangeStart && reportDateRangeEnd
                  ? `${reportDateRangeStart} — ${reportDateRangeEnd}`
                  : "Set in Date Range step"}
              </div>
              <div data-testid="cover-page-derived-client">
                <span className="font-medium">Client: </span>
                {selectedClientName ?? "Selected client"}
              </div>
            </div>
            <div className="mt-2 flex items-start gap-1 text-[11px] text-muted-foreground">
              <Info size={10} className="shrink-0 mt-0.5" />
              These values are automatically populated from your report settings
            </div>
          </div>
        </div>
      )}

      {/* Top Posts Config (Story 5.15) */}
      {widget.type === "top-posts" && (
        <div className="border-t-[0.5px] border-input/70 pt-4 mt-2" data-testid="top-posts-config">
          <h4 className="text-[13.5px] font-medium text-foreground mb-3">
            Post Settings
          </h4>

          {/* Platform selector */}
          <div className="mb-3">
            <label htmlFor="top-posts-platform" className="text-[13px] text-foreground">
              Platform
            </label>
            <div className="mt-1">
              <SelectField
                ariaLabel="Platform"
                testId="top-posts-platform-select"
                value={(widget.config as Record<string, unknown>).platform as string ?? "all"}
                onChange={(v) => handleTopPostsConfigChange("platform", v)}
                triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
                options={[
                  { value: "all", label: "All Connected Social" },
                  { value: "facebook-pages", label: "Facebook" },
                  { value: "instagram-business", label: "Instagram" },
                  { value: "linkedin-pages", label: "LinkedIn" },
                ]}
              />
            </div>
          </div>

          {/* Post count */}
          <div className="mb-3">
            <label htmlFor="top-posts-count" className="text-[13px] text-foreground">
              Number of Posts ({(widget.config as Record<string, unknown>).postCount as number ?? 5})
            </label>
            <input
              id="top-posts-count"
              data-testid="top-posts-count-input"
              type="range"
              min={3}
              max={10}
              value={(widget.config as Record<string, unknown>).postCount as number ?? 5}
              onChange={(e) => handleTopPostsConfigChange("postCount", parseInt(e.target.value, 10))}
              className="mt-1 w-full"
            />
          </div>

          {/* Sort metric */}
          <div className="mb-3">
            <label htmlFor="top-posts-sort" className="text-[13px] text-foreground">
              Sort By
            </label>
            <div className="mt-1">
              <SelectField
                ariaLabel="Sort By"
                testId="top-posts-sort-select"
                value={(widget.config as Record<string, unknown>).sortBy as string ?? "engagement"}
                onChange={(v) => handleTopPostsConfigChange("sortBy", v)}
                triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
                options={[
                  { value: "engagement", label: "Engagement" },
                  { value: "reach", label: "Reach" },
                  { value: "clicks", label: "Clicks" },
                ]}
              />
            </div>
            <div className="mt-1.5 flex items-start gap-1 text-[11px] text-muted-foreground">
              <Info size={10} className="shrink-0 mt-0.5" />
              {(widget.config as Record<string, unknown>).sortBy === "reach"
                ? "Total impressions/views the post received"
                : (widget.config as Record<string, unknown>).sortBy === "clicks"
                  ? "Total clicks on the post (link clicks, photo clicks, etc.)"
                  : "Total reactions + comments + shares (or saves for Instagram)"}
            </div>
          </div>
        </div>
      )}

      {/* Data Table Config (Story 12.3) — limit, sortBy, sortDirection for all data-table widgets */}
      {widget.type === "data-table" && (
        <div className="border-t-[0.5px] border-input/70 pt-4 mt-2" data-testid="data-table-config">
          <h4 className="text-[13.5px] font-medium text-foreground mb-3">
            Table Settings
          </h4>

          {/* Row Limit */}
          <div className="mb-3">
            <label htmlFor="data-table-limit" className="text-[13px] text-foreground">
              Row Limit
            </label>
            <div className="mt-1">
              <SelectField
                ariaLabel="Row Limit"
                testId="data-table-limit-select"
                value={String((widget.config as Record<string, unknown>).limit as number ?? 10)}
                onChange={(v) => {
                  onUpdate(widget.id, {
                    config: { ...widget.config, limit: Number(v) },
                  });
                }}
                triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
                options={[
                  { value: "3", label: "Top 3" },
                  { value: "5", label: "Top 5" },
                  { value: "10", label: "Top 10" },
                  { value: "25", label: "Top 25" },
                  { value: "50", label: "Top 50" },
                ]}
              />
            </div>
          </div>

          {/* Sort By — derived from widget's metricKeys, not hardcoded */}
          <div className="mb-3">
            <label htmlFor="data-table-sort" className="text-[13px] text-foreground">
              Sort By
            </label>
            <div className="mt-1">
              <SelectField
                ariaLabel="Sort By"
                testId="data-table-sort-select"
                value={(widget.config as Record<string, unknown>).sortBy as string ?? "date"}
                onChange={(v) => {
                  const newSortBy = v;
                  const isEmailPlatform = EMAIL_SORT_PLATFORMS.has(
                    (widget.config as Record<string, unknown>).integrationPlatform as string ?? ""
                  );
                  const isOpenMetric = newSortBy === "average_open_rate" || newSortBy === "open_count";
                  onUpdate(widget.id, {
                    config: {
                      ...widget.config,
                      sortBy: newSortBy,
                      ...(isEmailPlatform ? { showPrivacyDisclaimer: isOpenMetric } : {}),
                    },
                  });
                }}
                triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
                options={(() => {
                  const opts = [{ value: "date", label: "Date" }];
                  const metricKeys = (widget.config as Record<string, unknown>).metricKeys as string[] | undefined;
                  if (metricKeys && metricKeys.length > 0) {
                    const metricLabelMap = new Map(allMetrics.map((m) => [m.key, m.label]));
                    for (const key of metricKeys) {
                      opts.push({
                        value: key,
                        label: metricLabelMap.get(key) ?? key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
                      });
                    }
                  }
                  return opts;
                })()}
              />
            </div>
            {EMAIL_SORT_PLATFORMS.has((widget.config as Record<string, unknown>).integrationPlatform as string ?? "") &&
              ((widget.config as Record<string, unknown>).sortBy === "average_open_rate" || (widget.config as Record<string, unknown>).sortBy === "open_count") && (
              <div className="mt-1.5 flex items-start gap-1 text-[11px] text-muted-foreground">
                <Info size={10} className="shrink-0 mt-0.5" />
                Apple Mail Privacy Protection may inflate open metrics
              </div>
            )}
          </div>

          {/* Sort Direction */}
          <div className="mb-3">
            <label htmlFor="data-table-sort-dir" className="text-[13px] text-foreground">
              Sort Direction
            </label>
            <div className="mt-1">
              <SelectField
                ariaLabel="Sort Direction"
                testId="data-table-sort-dir-select"
                value={(widget.config as Record<string, unknown>).sortDirection as string ?? "desc"}
                onChange={(v) => {
                  onUpdate(widget.id, {
                    config: { ...widget.config, sortDirection: v },
                  });
                }}
                triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
                options={[
                  { value: "desc", label: "Descending (High → Low)" },
                  { value: "asc", label: "Ascending (Low → High)" },
                ]}
              />
            </div>
          </div>
        </div>
      )}

      {/* Roll-up Table Config (Story 40-5) — pick the metric COLUMNS + the
          totals-vs-averages footer. Each row is a member client. */}
      {widget.type === "rollup-table" && (
        <div className="border-t-[0.5px] border-input/70 pt-4 mt-2" data-testid="rollup-table-config">
          <h4 className="text-[13.5px] font-medium text-foreground mb-2">Roll-up Table</h4>
          <p className="text-[12px] text-muted-foreground mb-3">
            One row per member client. Pick the metric columns; the footer shows the
            roll-up total (or average).
          </p>
          <label className="block text-[13px] text-foreground mb-1.5">Metric columns</label>
          <div className="max-h-48 overflow-y-auto rounded-lg border-[0.5px] border-input p-2 space-y-2">
            {dynamicGroupedOptions.map((group) => (
              <div key={group.platform}>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{group.label}</div>
                {group.metrics.map((metricKey) => {
                  const selected = ((widget.config as Record<string, unknown>).metrics as string[] | undefined) ?? [];
                  const checked = selected.includes(metricKey);
                  return (
                    <label key={metricKey} className="flex items-center gap-2 py-0.5 text-[13px] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const next = checked ? selected.filter((m) => m !== metricKey) : [...selected, metricKey];
                          onUpdate(widget.id, { config: { ...widget.config, metrics: next } });
                        }}
                      />
                      <span className="truncate">{metricKey.replace(/[_-]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</span>
                    </label>
                  );
                })}
              </div>
            ))}
          </div>
          <label className="flex items-center gap-2 mt-3 text-[13px] cursor-pointer" data-testid="rollup-table-averages">
            <input
              type="checkbox"
              checked={(widget.config as Record<string, unknown>).showAverages === true}
              onChange={(e) =>
                onUpdate(widget.id, { config: { ...widget.config, showAverages: e.target.checked } })
              }
            />
            <span>Footer shows averages (instead of totals)</span>
          </label>
        </div>
      )}

      {/* Funnel Config Section (Story 5.18) */}
      {widget.type === "funnel" && (
        <div className="border-t-[0.5px] border-input/70 pt-4 mt-2" data-testid="funnel-config-section">
          <h4 className="text-[13.5px] font-medium text-foreground mb-2">
            Funnel Stages
          </h4>
          <p className="text-xs text-muted-foreground">
            Configure funnel stages in report data settings. Default funnel shows Sessions → Engaged → Leads → Conversions.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <label className="text-[13px] text-foreground" htmlFor="funnel-dropoff-toggle">
              Show drop-off rates
            </label>
            <input
              id="funnel-dropoff-toggle"
              type="checkbox"
              className="h-3.5 w-3.5 rounded"
              checked={(widget.config as Record<string, unknown>).showDropOffRates !== false}
              onChange={(e) => {
                onUpdate(widget.id, {
                  config: { ...widget.config, showDropOffRates: e.target.checked },
                });
              }}
              data-testid="funnel-dropoff-toggle"
            />
          </div>
        </div>
      )}

      {/* Demographic Config Section (Story 5.19) */}
      {widget.type === "demographic" && (
        <div className="border-t-[0.5px] border-input/70 pt-4 mt-2" data-testid="demographic-config-section">
          <h4 className="text-[13.5px] font-medium text-foreground mb-3">
            Demographic Settings
          </h4>

          {/* Mode selector */}
          <div className="mb-3">
            <label htmlFor="demographic-mode" className="text-[13px] text-foreground">
              Visualization Mode
            </label>
            <div className="mt-1">
              <SelectField
                ariaLabel="Visualization Mode"
                testId="demographic-mode-select"
                value={(widget.config as Record<string, unknown>).mode as string ?? "device-breakdown"}
                onChange={(v) => handleConfigChange("mode", v)}
                triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
                options={[
                  { value: "device-breakdown", label: "Device Breakdown" },
                  { value: "new-vs-returning", label: "New vs Returning" },
                  { value: "geographic-regions", label: "Geographic Regions" },
                  { value: "age-gender", label: "Age & Gender" },
                ]}
              />
            </div>
          </div>

          {/* Granularity toggle (geographic-regions only) */}
          {(widget.config as Record<string, unknown>).mode === "geographic-regions" && (
            <div className="mb-3">
              <label htmlFor="demographic-granularity" className="text-[13px] text-foreground">
                Granularity
              </label>
              <div className="mt-1">
                <SelectField
                  ariaLabel="Granularity"
                  testId="demographic-granularity-select"
                  value={(widget.config as Record<string, unknown>).granularity as string ?? "country"}
                  onChange={(v) => handleConfigChange("granularity", v)}
                  triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
                  options={[
                    { value: "country", label: "Country" },
                    { value: "city", label: "City" },
                  ]}
                />
              </div>
            </div>
          )}

          {/* Show trend indicator toggle */}
          <div className="mt-2 flex items-center gap-2">
            <label className="text-[13px] text-foreground" htmlFor="demographic-trend-toggle">
              Show trend indicator
            </label>
            <input
              id="demographic-trend-toggle"
              type="checkbox"
              className="h-3.5 w-3.5 rounded"
              checked={(widget.config as Record<string, unknown>).showTrendIndicator !== false}
              onChange={(e) => {
                onUpdate(widget.id, {
                  config: { ...widget.config, showTrendIndicator: e.target.checked },
                });
              }}
              data-testid="demographic-trend-toggle"
            />
          </div>
        </div>
      )}

      {/* Content Block Config (Story 5.24 → lifted to shared ContentBlockConfigSection in Story 37.4).
          Renders heading / paragraph / rich-text / spacer-divider fields (branches internally on
          widget.type; renders nothing for other types). One definition shared with
          ContentBlocksCategoryPanel so the two surfaces can't drift (AC #2/#3/#8). */}
      <ContentBlockConfigSection widget={widget} onUpdate={onUpdate} />

      {/* Image Config (Story 5.25) */}
      {widget.type === "image" && (
        <ImageConfigSection widget={widget} onUpdate={onUpdate} />
      )}

      {/* Embed Config (Story 5.26) */}
      {widget.type === "embed" && (
        <EmbedConfigSection widget={widget} onUpdate={onUpdate} />
      )}

      {/* Custom Metric Config (Story 5.28) */}
      {widget.type === "custom-metric" && (
        <CustomMetricConfigSection widget={widget} onUpdate={onUpdate} groupedMetricOptions={dynamicGroupedOptions} />
      )}

      {/* Benchmark Config (Story 5.29) */}
      {widget.type === "benchmark" && (
        <BenchmarkConfigSection widget={widget} onUpdate={onUpdate} />
      )}

      {/* Goal Config (Story 5.30) */}
      {widget.type === "goal" && (
        <GoalConfigSection widget={widget} onUpdate={onUpdate} metricPlatformMap={dynamicMetricPlatformMap} />
      )}

      {/* Data Source Configuration (Story 5.34) — hidden when the settings search
          doesn't match its keywords (integration / metric / group by). */}
      {DATA_CONNECTED_WIDGET_TYPES.includes(widget.type) && dataSourceVisible && (
        <DataSourceSection widget={widget} onUpdate={onUpdate} clientId={clientId} />
      )}

      {/* KPI Config (Story 8.11) — the "Show industry benchmark" opt-in. Sits
          with the data config since the indicator is a data overlay. */}
      {isKpi && (
        <KpiConfigSection widget={widget} onUpdate={onUpdate} />
      )}

      {/* Annotations Section (Story 5.17) — line-chart only. Restyled to match the
          rest of the panel (Epic 37): 13.5px text, h-9 rounded-lg inputs, neutral
          active states (no brand red), airy list rows. */}
      {isLineChart && (
        <div className="border-t-[0.5px] border-input/70 pt-4 mt-2" data-testid="annotations-section">
          {/* Always open — no collapse toggle. */}
          <div className="flex w-full items-center justify-between" data-testid="annotations-toggle">
            <span className="text-[13.5px] font-medium text-foreground">
              Annotations <span className="text-foreground">({annotations.length}/{MAX_ANNOTATIONS})</span>
            </span>
          </div>

            <div className="mt-4 space-y-3">
              {/* Annotation list */}
              {annotations.length === 0 && !showAnnotationForm && (
                <p className="text-[13px] text-foreground" data-testid="annotations-empty">
                  No annotations yet.
                </p>
              )}

              <div aria-live="polite" className="space-y-2">
                {annotations.map((ann) => (
                  <div
                    key={ann.id}
                    data-testid={`annotation-item-${ann.id}`}
                    className="flex h-9 items-center gap-2.5 rounded-lg border-[0.5px] border-input bg-background px-3"
                  >
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: ANNOTATION_COLORS[ann.color] }}
                    />
                    {ann.icon && ANNOTATION_ICON_MAP[ann.icon] && (
                      (() => {
                        const Icon = ANNOTATION_ICON_MAP[ann.icon]!;
                        return <Icon size={14} className="shrink-0 text-muted-foreground" />;
                      })()
                    )}
                    {/* Date sits with the label; the edit/delete pair is pinned to
                        the far right, tight together. */}
                    <span className="min-w-0 flex-1 truncate text-[13px] text-foreground" title={ann.label}>{ann.label}</span>
                    <span className="ml-auto shrink-0 text-[11px] text-foreground">
                      {formatAnnotationDate(ann.date)}
                    </span>
                    {/* Edit + delete grouped at the end. The press target is just the
                        icon (no big square padding) — inline-flex, no fixed box.
                        Icon colour matches the sidebar icons (text-foreground). */}
                    <div className="flex shrink-0 items-center gap-2.5">
                      <button
                        type="button"
                        data-testid={`annotation-edit-${ann.id}`}
                        aria-label={`Edit ${ann.label}`}
                        onClick={() => handleAnnotationEdit(ann)}
                        className="inline-flex touch-target-sm text-foreground hover:opacity-70 cursor-pointer"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        data-testid={`annotation-delete-${ann.id}`}
                        aria-label={`Delete ${ann.label}`}
                        onClick={() => handleAnnotationDelete(ann.id)}
                        className="inline-flex touch-target-sm text-foreground hover:text-destructive cursor-pointer"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Annotation button */}
              {!showAnnotationForm && (
                <div>
                  <button
                    type="button"
                    data-testid="annotation-add-btn"
                    disabled={annotations.length >= MAX_ANNOTATIONS}
                    onClick={handleAnnotationAdd}
                    className="flex h-9 w-full touch-target-sm items-center justify-center gap-1.5 rounded-lg border-[0.5px] border-dashed border-input text-[13.5px] text-foreground hover:bg-accent/50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <Plus size={14} />
                    Add annotation
                  </button>
                  {annotations.length >= MAX_ANNOTATIONS && (
                    <p className="mt-1.5 text-[11px] text-muted-foreground" data-testid="annotation-max-message">
                      Maximum {MAX_ANNOTATIONS} annotations per chart
                    </p>
                  )}
                </div>
              )}

              {/* Annotation Form (inline) */}
              {showAnnotationForm && (
                <div className="space-y-3 rounded-lg border-[0.5px] border-input bg-background p-3" data-testid="annotation-form">
                  {/* Date picker */}
                  <div className="space-y-1.5">
                    <label htmlFor="annotation-date" className="block text-[13px] text-foreground">
                      Date
                    </label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid="annotation-date-trigger"
                          className="flex h-9 w-full touch-target-sm items-center gap-1.5 rounded-lg border-[0.5px] border-input bg-background px-2.5 text-left text-[13.5px] shadow-xs cursor-pointer"
                        >
                          <CalendarDays size={14} className="text-muted-foreground" />
                          {formDate
                            ? formDate.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
                            : <span className="text-muted-foreground">Select date</span>}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formDate}
                          onSelect={(d: Date | undefined) => setFormDate(d)}
                          disabled={(d: Date) => {
                            if (dateRangeStart && d < dateRangeStart) return true;
                            if (dateRangeEnd && d > dateRangeEnd) return true;
                            return false;
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Label */}
                  <div className="space-y-1.5">
                    <label htmlFor="annotation-label" className="block text-[13px] text-foreground">
                      Label <span className="text-muted-foreground">({formLabel.length}/50)</span>
                    </label>
                    <Input
                      id="annotation-label"
                      data-testid="annotation-label-input"
                      type="text"
                      maxLength={50}
                      value={formLabel}
                      onChange={(e) => setFormLabel(e.target.value)}
                      placeholder="e.g., Campaign Launch"
                      className="h-9 rounded-lg text-[13.5px]"
                    />
                  </div>

                  {/* Description (optional) */}
                  <div className="space-y-1.5">
                    <label htmlFor="annotation-description" className="block text-[13px] text-foreground">
                      Description <span className="text-muted-foreground">({formDescription.length}/200)</span>
                    </label>
                    <textarea
                      id="annotation-description"
                      data-testid="annotation-description-input"
                      maxLength={200}
                      rows={2}
                      value={formDescription}
                      onChange={(e) => setFormDescription(e.target.value)}
                      placeholder="Optional description shown on hover"
                      className="w-full resize-none rounded-lg border-[0.5px] border-input bg-background px-2.5 py-2 text-[13.5px] shadow-xs focus:outline-none focus-visible:outline-none"
                    />
                  </div>

                  {/* Color — fixed palette of round swatches. touch-target-sm so the
                      global button min-size rule doesn't inflate them to 44px. */}
                  <div className="space-y-1.5">
                    <label className="block text-[13px] text-foreground">Color</label>
                    <div className="flex gap-2">
                      {ANNOTATION_COLOR_OPTIONS.map((colorKey) => (
                        <button
                          key={colorKey}
                          type="button"
                          data-testid={`annotation-color-${colorKey}`}
                          title={ANNOTATION_COLOR_LABELS[colorKey]}
                          aria-label={`Color: ${ANNOTATION_COLOR_LABELS[colorKey]}`}
                          onClick={() => setFormColor(colorKey)}
                          className={`block h-7 w-7 touch-target-sm rounded-full transition-shadow cursor-pointer ${
                            formColor === colorKey
                              ? "ring-2 ring-foreground ring-offset-2 ring-offset-background"
                              : "ring-1 ring-input hover:ring-foreground/40"
                          }`}
                          style={{ backgroundColor: ANNOTATION_COLORS[colorKey] }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Icon (optional) */}
                  <div className="space-y-1.5">
                    <label className="block text-[13px] text-foreground">Icon <span className="text-muted-foreground">(optional)</span></label>
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        data-testid="annotation-icon-none"
                        onClick={() => setFormIcon("")}
                        className={`flex h-9 touch-target-sm items-center rounded-lg border-[0.5px] px-3 text-[13px] cursor-pointer ${
                          formIcon === ""
                            ? "border-input bg-foreground/10 text-foreground"
                            : "border-input bg-background text-muted-foreground hover:bg-accent/50"
                        }`}
                      >
                        None
                      </button>
                      {ANNOTATION_ICON_OPTIONS.map((iconKey) => {
                        const Icon = ANNOTATION_ICON_MAP[iconKey]!;
                        return (
                          <button
                            key={iconKey}
                            type="button"
                            data-testid={`annotation-icon-${iconKey}`}
                            title={ANNOTATION_ICON_LABELS[iconKey]}
                            aria-label={`Icon: ${ANNOTATION_ICON_LABELS[iconKey]}`}
                            onClick={() => setFormIcon(iconKey)}
                            className={`flex h-9 w-9 touch-target-sm items-center justify-center rounded-lg border-[0.5px] cursor-pointer ${
                              formIcon === iconKey
                                ? "border-input bg-foreground/10 text-foreground"
                                : "border-input bg-background text-muted-foreground hover:bg-accent/50"
                            }`}
                          >
                            <Icon size={15} />
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Error message */}
                  {formError && (
                    <p className="text-[12px] text-destructive" data-testid="annotation-form-error" role="alert">
                      {formError}
                    </p>
                  )}

                  {/* Save / Cancel */}
                  <div className="flex gap-2 pt-0.5">
                    <button
                      type="button"
                      data-testid="annotation-save-btn"
                      onClick={handleAnnotationSave}
                      className="h-9 flex-1 touch-target-sm rounded-lg bg-foreground text-[13.5px] font-medium text-background hover:bg-foreground/90 cursor-pointer"
                    >
                      {editingAnnotationId ? "Update" : "Save"}
                    </button>
                    <button
                      type="button"
                      data-testid="annotation-cancel-btn"
                      onClick={resetForm}
                      className="h-9 flex-1 touch-target-sm rounded-lg border-[0.5px] border-input text-[13.5px] text-foreground hover:bg-accent/50 cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
        </div>
      )}

      {/* Appearance — LAST in the panel. Per-widget Fill / Border colour + Border
          radius, stored under config.style; applied on the canvas card and mirrored
          in the PDF renderer. Excluded for spacer-divider (its config.style is a
          string variant) and cover-page (no card). Field labels share the navy
          "Appearance" heading colour (text-foreground). */}
      {/* Settings-search empty-state — shown only when NOTHING matches (neither the
          Data source block nor any Appearance group). Lives at panel level so it
          isn't tied to the Appearance wrapper (which is hidden when no group matches). */}
      {!isCoverPage && widget.type !== "spacer-divider" &&
        settingsQuery &&
        !dataSourceVisible &&
        !groupVisible("Background & Border") &&
        !groupVisible("Text") &&
        !(isSeriesChart && seriesMetricKeys.length > 0 && groupVisible("Series")) &&
        !(isDataConnected && groupVisible("Source")) &&
        !groupVisible("Title") && (
          <p className="text-[12.5px] text-muted-foreground" data-testid="settings-search-empty">
            No settings match “{settingsSearch.trim()}”.
          </p>
        )}

      {!isCoverPage && widget.type !== "spacer-divider" &&
        (groupVisible("Background & Border") || groupVisible("Text") || (isSeriesChart && seriesMetricKeys.length > 0 && groupVisible("Series")) || (isDataConnected && groupVisible("Source")) || groupVisible("Title")) && (
        <div className="space-y-4" data-testid="appearance-config-section">

          {/* Divider between the Data source block (Integration / Selected metrics)
              above and the Appearance groups below. Only when a data source rendered.
              Space BELOW the line so the "Background & Border" label isn't cramped
              against it — matches the pt-4 breathing room of the section dividers. */}
          {isDataConnected && dataSourceVisible && (
            <hr className="border-t-[0.5px] border-input/70 mt-2 mb-4" />
          )}

          {/* ── Group: Background & Border ─────────────────────────── */}
          {groupVisible("Background & Border") && (
          <div className="space-y-3">
            <label className="block text-[13.5px] font-medium">Background &amp; Border</label>

            {fieldVisible("Fill color", "Background & Border") && (
            <div className="grid grid-cols-[1fr_130px] items-center gap-2">
              <span className="min-w-0 text-[13px] text-foreground">Fill color</span>
              <div className="w-full">
                <ColorPickerField
                  ariaLabel="Fill color"
                  testId="style-fill-color"
                  value={widgetStyle.fillColor}
                  defaultValue="#ffffff"
                  onChange={(hex) => handleStyleChange("fillColor", hex)}
                  onReset={() => handleStyleChange("fillColor", undefined)}
                />
              </div>
            </div>
            )}

            {fieldVisible("Border color", "Background & Border") && (
            <div className="grid grid-cols-[1fr_130px] items-center gap-2">
              <span className="min-w-0 text-[13px] text-foreground">Border color</span>
              <div className="w-full">
                <ColorPickerField
                  ariaLabel="Border color"
                  testId="style-border-color"
                  value={widgetStyle.borderColor}
                  defaultValue="#c0cad8"
                  onChange={(hex) => handleStyleChange("borderColor", hex)}
                  onReset={() => handleStyleChange("borderColor", undefined)}
                />
              </div>
            </div>
            )}

            {fieldVisible("Border radius", "Background & Border") && (
            <div className="grid grid-cols-[1fr_130px] items-center gap-2">
              <span className="min-w-0 text-[13px] text-foreground">Border radius</span>
              <PxField
                ariaLabel="Border radius (px)"
                testId="style-border-radius"
                value={widgetStyle.borderRadius ?? ""}
                placeholder="22"
                min={0}
                max={48}
                onChange={(n) => handleStyleChange("borderRadius", n)}
              />
            </div>
            )}
          </div>
          )}

          {/* ── Group: Text (charts: "Axis labels") ────────────────── */}
          {groupVisible("Text") && (
          <div className={`space-y-3 ${groupVisible("Background & Border") ? "border-t-[0.5px] border-input/70 pt-4 mt-2" : ""}`}>
            <label className="block text-[13.5px] font-medium">{textGroupLabel}</label>

            {fieldVisible("Text color", "Text") && (
            <div className="grid grid-cols-[1fr_130px] items-center gap-2">
              <span className="min-w-0 text-[13px] text-foreground">{isAxisChart ? "Axis color" : "Text color"}</span>
              <div className="w-full">
                <ColorPickerField
                  ariaLabel="Text color"
                  testId="style-text-color"
                  value={widgetStyle.textColor}
                  defaultValue="#1e2a44"
                  onChange={(hex) => handleStyleChange("textColor", hex)}
                  onReset={() => handleStyleChange("textColor", undefined)}
                />
              </div>
            </div>
            )}

            {fieldVisible("Font", "Text") && (
            <div className="grid grid-cols-[1fr_130px] items-center gap-2">
              <span className="min-w-0 text-[13px] text-foreground">{isAxisChart ? "Axis font" : "Font"}</span>
              <SelectField
                ariaLabel="Font"
                testId="style-font-family"
                value={widgetStyle.fontFamily ?? DEFAULT_WIDGET_FONT}
                onChange={(v) => handleStyleChange("fontFamily", v === DEFAULT_WIDGET_FONT ? undefined : v)}
                triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
                options={WIDGET_FONT_OPTIONS.map((f) => ({ value: f.family, label: f.label, style: { fontFamily: f.family } }))}
              />
            </div>
            )}

            {fieldVisible("Text size", "Text") && (
            <div className="grid grid-cols-[1fr_130px] items-center gap-2">
              <span className="min-w-0 text-[13px] text-foreground">{isAxisChart ? "Axis text size" : "Text size"}</span>
              <PxField
                ariaLabel="Text size (px)"
                testId="style-font-size"
                value={widgetStyle.fontSize ?? ""}
                placeholder="auto"
                min={8}
                max={isAxisChart ? 24 : 120}
                onChange={(n) => handleStyleChange("fontSize", n)}
              />
            </div>
            )}

            {/* KPI only: hide the in-card trend line (sparkline). Stored as
                style.hideSparkline; absent = shown. */}
            {isKpi && fieldVisible("Trend line", "Text") && (
            <div className="grid grid-cols-[1fr_130px] items-center gap-2">
              <span className="min-w-0 text-[13px] text-foreground">Trend line</span>
              <div className="flex w-full justify-end">
                <Switch
                  aria-label="Show trend line"
                  data-testid="style-show-sparkline"
                  // Override the theme's primary (red here) with the report's dark
                  // navy so it matches the design system rather than the alert hue.
                  className="data-[state=checked]:bg-[#1e2a44]"
                  checked={!(widget.config?.style as { hideSparkline?: boolean } | undefined)?.hideSparkline}
                  onCheckedChange={(on) => handleStyleChange("hideSparkline", on ? undefined : true)}
                />
              </div>
            </div>
            )}
          </div>
          )}

          {/* ── Group: Series colors (line/bar) — one colour per bound metric ── */}
          {isSeriesChart && seriesMetricKeys.length > 0 && groupVisible("Series") && (
          <div className={`space-y-3 ${groupVisible("Background & Border") || groupVisible("Text") ? "border-t-[0.5px] border-input/70 pt-4 mt-2" : ""}`}>
            <label className="block text-[13.5px] font-medium">{widget.type === "bar-chart" ? "Bars" : "Lines"}</label>
            {seriesMetricKeys.map((mKey, i) => (
              fieldVisible(mKey, "Series") && (
              // Per-series block: the bound metric key as a quiet caption, then a
              // labelled Name row + Color row (same label-left / control-right
              // pattern as the rest of the panel — not crammed on one line).
              <div key={mKey} className={`space-y-2 ${i > 0 ? "border-t-[0.5px] border-input/70 pt-2.5" : ""}`}>
                <div className="grid grid-cols-[1fr_130px] items-center gap-2">
                  <span className="min-w-0 text-[13px] text-foreground">{widget.type === "bar-chart" ? "Bar name" : "Line name"}</span>
                  <Input
                    type="text"
                    aria-label={`Label for ${mKey}`}
                    data-testid={`series-label-${mKey}`}
                    value={seriesLabels[mKey] ?? ""}
                    placeholder={mKey}
                    onChange={(e) => handleSeriesLabelChange(mKey, e.target.value)}
                    className="h-9 w-full rounded-lg text-[13.5px]"
                  />
                </div>
                <div className="grid grid-cols-[1fr_130px] items-center gap-2">
                  <span className="min-w-0 text-[13px] text-foreground">{widget.type === "bar-chart" ? "Bar color" : "Line color"}</span>
                  <div className="w-full">
                    <ColorPickerField
                      ariaLabel={`Color for ${mKey}`}
                      testId={`series-color-${mKey}`}
                      value={seriesColors[mKey]}
                      defaultValue={NAVY_SERIES[i % NAVY_SERIES.length]}
                      onChange={(hex) => handleSeriesColorChange(mKey, hex)}
                      onReset={() => handleSeriesColorChange(mKey, undefined)}
                    />
                  </div>
                </div>
              </div>
              )
            ))}

            {/* Separator + sub-header for the global legend options. */}
            {seriesMetricKeys.length > 0 && <hr className="border-t-[0.5px] border-input/70" />}
            <label className="block text-[13.5px] font-medium">Legend</label>

            {/* Legend text size — global for the chart. */}
            {fieldVisible("Legend size", "Series") && (
            <div className="grid grid-cols-[1fr_130px] items-center gap-2">
              <span className="min-w-0 text-[13px] text-foreground">Legend size</span>
              <PxField
                ariaLabel="Legend size (px)"
                testId="legend-font-size"
                value={(widget.config?.style as { legendFontSize?: number } | undefined)?.legendFontSize ?? ""}
                placeholder="11"
                min={8}
                max={24}
                onChange={(n) => handleStyleChange("legendFontSize", n)}
              />
            </div>
            )}

            {/* Legend marker — the symbol left of each label: line (default), dot, or none. */}
            {fieldVisible("Legend marker", "Series") && (
            <div className="grid grid-cols-[1fr_130px] items-center gap-2">
              <span className="min-w-0 text-[13px] text-foreground">Legend marker</span>
              <SelectField
                ariaLabel="Legend marker"
                testId="legend-marker"
                value={(widget.config?.style as { legendMarker?: string } | undefined)?.legendMarker ?? "line"}
                onChange={(v) => handleStyleChange("legendMarker", v === "line" ? undefined : v)}
                triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
                options={[
                  { value: "line", label: "Line" },
                  { value: "dot", label: "Dot" },
                  { value: "none", label: "None" },
                ]}
              />
            </div>
            )}

            {/* Marker size — only when there IS a marker (line/dot). */}
            {fieldVisible("Marker size", "Series") &&
              ((widget.config?.style as { legendMarker?: string } | undefined)?.legendMarker ?? "line") !== "none" && (
            <div className="grid grid-cols-[1fr_130px] items-center gap-2">
              <span className="min-w-0 text-[13px] text-foreground">Marker size</span>
              <PxField
                ariaLabel="Marker size (px)"
                testId="legend-marker-size"
                value={(widget.config?.style as { legendMarkerSize?: number } | undefined)?.legendMarkerSize ?? ""}
                placeholder={((widget.config?.style as { legendMarker?: string } | undefined)?.legendMarker ?? "line") === "dot" ? "8" : "14"}
                min={2}
                max={24}
                onChange={(n) => handleStyleChange("legendMarkerSize", n)}
              />
            </div>
            )}
          </div>
          )}

          {/* ── Group: Source (integration badge) — data widgets only ── */}
          {isDataConnected && groupVisible("Source") && (
          <div className={`space-y-3 ${groupVisible("Background & Border") || groupVisible("Text") || (isSeriesChart && seriesMetricKeys.length > 0) ? "border-t-[0.5px] border-input/70 pt-4 mt-2" : ""}`}>
            <label className="block text-[13.5px] font-medium">Source</label>
            <div className="grid grid-cols-[1fr_130px] items-center gap-2">
              <span className="min-w-0 text-[13px] text-foreground">Icon</span>
              <SelectField
                ariaLabel="Icon"
                testId="integration-badge-mode"
                // Default is "logo" (logo only, no name). With 2+ integrations the
                // badge shows all logos and no names, so a stored "logo-name" reads
                // as "Logo" there too.
                value={(() => {
                  const v = (widget.config?.style as { integrationBadge?: string } | undefined)?.integrationBadge ?? "logo";
                  return badgeMultiSource && v === "logo-name" ? "logo" : v;
                })()}
                onChange={(v) => handleStyleChange("integrationBadge", v === "logo" ? undefined : v)}
                triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
                options={
                  badgeMultiSource
                    ? [
                        { value: "logo", label: "Logos" },
                        { value: "none", label: "Hidden" },
                      ]
                    : [
                        { value: "logo-name", label: "Logo + name" },
                        { value: "logo", label: "Logo only" },
                        { value: "none", label: "Hidden" },
                      ]
                }
              />
            </div>

            {/* Badge styling — only when the badge is shown. Icon size always;
                text color/font/size only when the name is shown (logo-name). */}
            {((widget.config?.style as { integrationBadge?: string } | undefined)?.integrationBadge ?? "logo") !== "none" && (
              <>
                <div className="grid grid-cols-[1fr_130px] items-center gap-2">
                  <span className="min-w-0 text-[13px] text-foreground">Icon size</span>
                  <PxField
                    ariaLabel="Badge icon size (px)"
                    testId="badge-icon-size"
                    value={badgeStyle.iconSize ?? ""}
                    placeholder="20"
                    min={10}
                    max={48}
                    onChange={(n) => handleBadgeStyleChange("iconSize", n)}
                  />
                </div>

                {/* Text controls only apply to a single-source badge (multi shows
                    only logos, no name). */}
                {!badgeMultiSource && ((widget.config?.style as { integrationBadge?: string } | undefined)?.integrationBadge ?? "logo") === "logo-name" && (
                  <>
                    <div className="grid grid-cols-[1fr_130px] items-center gap-2">
                      <span className="min-w-0 text-[13px] text-foreground">Text color</span>
                      <div className="w-full">
                        <ColorPickerField
                          ariaLabel="Badge text color"
                          testId="badge-text-color"
                          value={badgeStyle.color}
                          defaultValue="#7589a8"
                          onChange={(hex) => handleBadgeStyleChange("color", hex)}
                          onReset={() => handleBadgeStyleChange("color", undefined)}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-[1fr_130px] items-center gap-2">
                      <span className="min-w-0 text-[13px] text-foreground">Text font</span>
                      <SelectField
                        ariaLabel="Badge text font"
                        testId="badge-font-family"
                        value={badgeStyle.fontFamily ?? DEFAULT_WIDGET_FONT}
                        onChange={(v) => handleBadgeStyleChange("fontFamily", v === DEFAULT_WIDGET_FONT ? undefined : v)}
                        triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
                        searchable={true}
                        options={WIDGET_FONT_OPTIONS.map((f) => ({ value: f.family, label: f.label, style: { fontFamily: f.family } }))}
                      />
                    </div>

                    <div className="grid grid-cols-[1fr_130px] items-center gap-2">
                      <span className="min-w-0 text-[13px] text-foreground">Text size</span>
                      <PxField
                        ariaLabel="Badge text size (px)"
                        testId="badge-font-size"
                        value={badgeStyle.fontSize ?? ""}
                        placeholder="11"
                        min={8}
                        max={32}
                        onChange={(n) => handleBadgeStyleChange("fontSize", n)}
                      />
                    </div>
                  </>
                )}
              </>
            )}

            {!hasBoundIntegration && (
              <p className="text-[11px] text-muted-foreground">Bind an integration to show the badge.</p>
            )}
          </div>
          )}

          {/* ── Group: Title ───────────────────────────────────────── */}
          {groupVisible("Title") && (
          <div className={`space-y-3 ${groupVisible("Background & Border") || groupVisible("Text") || (isSeriesChart && seriesMetricKeys.length > 0) || isDataConnected ? "border-t-[0.5px] border-input/70 pt-4 mt-2" : ""}`}>
            <label className="block text-[13.5px] font-medium">Title</label>

            {fieldVisible("Title name", "Title") && (
            <div className="grid grid-cols-[1fr_130px] items-center gap-2">
              <span className="min-w-0 text-[13px] text-foreground">Title name</span>
              <Input
                id="widget-title-input"
                data-testid="widget-title-input"
                type="text"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={handleTitleCommit}
                onKeyDown={handleTitleKeyDown}
                className="h-9 w-full rounded-lg text-[13.5px]"
              />
            </div>
            )}

            {fieldVisible("Subtitle", "Title") && (
            <div className="grid grid-cols-[1fr_130px] items-center gap-2">
              <span className="min-w-0 text-[13px] text-foreground">Subtitle</span>
              <Input
                data-testid="widget-subtitle-input"
                type="text"
                value={(widget.config?.subtitle as string) ?? ""}
                placeholder="Optional"
                onChange={(e) => handleConfigChange("subtitle", e.target.value)}
                className="h-9 w-full rounded-lg text-[13.5px]"
              />
            </div>
            )}

          {/* No "Show title" toggle — the title behaves like the subtitle: leave
              Title name empty and it simply disappears (canvas + PDF). The title
              appearance controls only matter when there IS a title. */}
          {titleDraft.trim() !== "" && (
            <>
              {fieldVisible("Title color", "Title") && (
              <div className="grid grid-cols-[1fr_130px] items-center gap-2">
                <span className="min-w-0 text-[13px] text-foreground">Title color</span>
                <div className="w-full">
                  <ColorPickerField
                    ariaLabel="Title color"
                    testId="title-color"
                    value={titleStyle.color}
                    defaultValue="#7589a8"
                    onChange={(hex) => handleTitleStyleChange("color", hex)}
                    onReset={() => handleTitleStyleChange("color", undefined)}
                  />
                </div>
              </div>
              )}

              {fieldVisible("Title size", "Title") && (
              <div className="grid grid-cols-[1fr_130px] items-center gap-2">
                <span className="min-w-0 text-[13px] text-foreground">Title size</span>
                <PxField
                  ariaLabel="Title size (px)"
                  testId="title-size"
                  value={titleStyle.fontSize ?? ""}
                  placeholder="13"
                  min={8}
                  max={48}
                  onChange={(n) => handleTitleStyleChange("fontSize", n)}
                />
              </div>
              )}

              {fieldVisible("Title font", "Title") && (
              <div className="grid grid-cols-[1fr_130px] items-center gap-2">
                <span className="min-w-0 text-[13px] text-foreground">Title font</span>
                <SelectField
                  ariaLabel="Title font"
                  testId="title-font-family"
                  value={titleStyle.fontFamily ?? DEFAULT_WIDGET_FONT}
                  onChange={(v) => handleTitleStyleChange("fontFamily", v === DEFAULT_WIDGET_FONT ? undefined : v)}
                  triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
                  searchable={true}
                  options={WIDGET_FONT_OPTIONS.map((f) => ({ value: f.family, label: f.label, style: { fontFamily: f.family } }))}
                />
              </div>
              )}

              {fieldVisible("Title align", "Title") && (
              <div className="grid grid-cols-[1fr_130px] items-center gap-2">
                <span className="min-w-0 text-[13px] text-foreground">Title align</span>
                {/* Segmented control sized like the other fields: full 130px column, h-9. */}
                <div className="flex h-9 w-full items-center gap-1 rounded-lg border-[0.5px] border-input p-1 shadow-xs">
                  {(["left", "center", "right"] as const).map((a) => (
                    <button
                      key={a}
                      type="button"
                      aria-label={`Align title ${a}`}
                      data-testid={`title-align-${a}`}
                      aria-pressed={(titleStyle.align ?? "left") === a}
                      onClick={() => handleTitleStyleChange("align", a === "left" ? undefined : a)}
                      className={`flex h-full flex-1 touch-target-sm items-center justify-center rounded-md text-[12px] leading-none cursor-pointer ${(titleStyle.align ?? "left") === a ? "bg-foreground/10 text-foreground" : "text-muted-foreground hover:bg-accent/50"}`}
                    >
                      {a === "left" ? "L" : a === "center" ? "C" : "R"}
                    </button>
                  ))}
                </div>
              </div>
              )}
            </>
          )}
          </div>
          )}
        </div>
      )}
    </div>
  );
}
